"use client";

import React, { useEffect, useState, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function CinematicVision() {
  const [offset, setOffset] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          setOffset(window.scrollY);
        }
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const strips = [{
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1616215/img/cinematic_strip_urban.png",
    speed: 0.15,
    mt: "0px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1616215/img/cinematic_strip_studio.png",
    speed: -0.2,
    mt: "100px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1616215/img/cinematic_strip_camera.png",
    speed: 0.1,
    mt: "0px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1616215/img/cinematic_strip_editing.png",
    speed: -0.15,
    mt: "150px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1616215/img/cinematic_strip_abstract.png",
    speed: 0.2,
    mt: "50px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1625243/img/cinematic_strip_production.png",
    speed: -0.1,
    mt: "80px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1625243/img/cinematic_strip_lighting.png",
    speed: 0.12,
    mt: "20px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1625243/img/cinematic_strip_color.png",
    speed: -0.18,
    mt: "120px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1625243/img/cinematic_strip_sound.png",
    speed: 0.08,
    mt: "40px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1625243/img/cinematic_strip_vfx.png",
    speed: -0.25,
    mt: "180px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1625243/img/cinematic_strip_direction.png",
    speed: 0.15,
    mt: "10px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1625243/img/cinematic_strip_script.png",
    speed: -0.12,
    mt: "90px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1625243/img/cinematic_strip_lens.png",
    speed: 0.22,
    mt: "60px"
  }, {
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1625243/img/cinematic_strip_grading.png",
    speed: -0.05,
    mt: "30px"
  }];
  return <section data-section-id="6929" id="cinematic-vision" ref={sectionRef} className="dark bg-background text-foreground relative min-h-screen w-full overflow-hidden flex items-center justify-center py-20">
      <video src="/img/cinematic_vision_background.mp4" autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover z-0" />
      
      <div className="absolute inset-0 z-10 grid grid-cols-5 gap-2 md:gap-4 px-2 md:px-8 opacity-20 pointer-events-none select-none">
        {strips.map((strip, i) => <div key={i} data-index={i} className="relative w-full h-[150%] -top-[25%] overflow-hidden rounded-none" style={{
        transform: `translateY(${offset * strip.speed}px)`,
        marginTop: strip.mt
      }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${strip.img})`
        }} />
            <div className="absolute inset-0 bg-black/40" />
          </div>)}
      </div>

      <div className="absolute inset-0 z-15 bg-[radial-gradient(circle_at_center,hsla(0,100%,50%,0.15)_0%,transparent_70%,var(--background)_95%)] pointer-events-none" />

      <div className="relative z-20 container mx-auto px-4 flex flex-col items-center text-center">
        <h1 className="font-serif text-5xl md:text-7xl lg:text-9xl font-bold tracking-tighter uppercase mb-8">
          <span className="bg-gradient-to-b from-white to-neutral-400 bg-clip-text text-transparent">Fanava</span>
        </h1>

        <p className="font-sans text-lg md:text-xl text-muted-foreground max-w-[60ch] mb-12 leading-relaxed uppercase tracking-widest">
          Precision in Every Frame. We craft immersive digital experiences that blend 
          cinematic storytelling with elite post-production technology.
        </p>

        <Button asChild size="lg" className="h-16 px-10 rounded-none text-lg font-medium bg-transparent border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-400 hover:scale-105 uppercase">
          <Link to="mailto:info@fanava.studio">
            Start Project
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </Button>
      </div>
    </section>;
}